import React, { useCallback } from "react";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

export interface IntensityValue {
  min?: number | null;
  max?: number | null;
}

interface IntensityFilterProps {
  value: IntensityValue;
  onChange: (value: IntensityValue) => void;
  disabled?: boolean;
}

const parseIntegerInput = (raw: string): number | undefined => {
  if (raw === "") return undefined;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return undefined;
  return Math.max(0, parsed);
};

export const IntensityFilter: React.FC<IntensityFilterProps> = React.memo(
  ({ value, onChange, disabled = false }) => {
    const handleMinChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const next = parseIntegerInput(event.target.value);
        onChange({ min: next, max: value.max });
      },
      [onChange, value.max]
    );

    const handleMaxChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const next = parseIntegerInput(event.target.value);
        onChange({ min: value.min, max: next });
      },
      [onChange, value.min]
    );

    const hasInversionError =
      value.min !== undefined &&
      value.min !== null &&
      value.max !== undefined &&
      value.max !== null &&
      value.min > value.max;

    return (
      <Stack spacing={1}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 60 }}>
            Minimum
          </Typography>
          <TextField
            size="small"
            type="number"
            value={value.min ?? ""}
            onChange={handleMinChange}
            disabled={disabled}
            inputProps={{ step: 1, min: 0 }}
            sx={{ width: 120 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 60 }}>
            Maximum
          </Typography>
          <TextField
            size="small"
            type="number"
            value={value.max ?? ""}
            onChange={handleMaxChange}
            disabled={disabled}
            inputProps={{ step: 1, min: 0 }}
            sx={{ width: 120 }}
          />
        </Stack>
        {hasInversionError && (
          <Typography variant="caption" color="error">
            Min must be ≤ max
          </Typography>
        )}
      </Stack>
    );
  }
);

IntensityFilter.displayName = "IntensityFilter";
